import { ArrowRight, Video, CalendarDays, Clock3, PlayCircle } from "lucide-react";
import { WHATSAPP_CHANNEL_URL } from "@/lib/brand";
import { browserTimezone, timezoneAbbreviation } from "@/lib/timezones";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";

/**
 * Community & Live Learning — two independent cards:
 *   - WhatsApp Channel: a fixed public link (WHATSAPP_CHANNEL_URL, from
 *     lib/brand.js), never admin-configurable.
 *   - Live Zoom Class: admin-configured via app_config (Admin > Modules >
 *     App Configuration). Its own card actually renders one of three
 *     states — an upcoming class, a recorded past session (recording_*
 *     fields, the same app_config document), or neither — see
 *     ZoomClassCard below. An upcoming class always takes priority over a
 *     recording when both are set, and each state carries its own small
 *     uppercase label ("UPCOMING LIVE CLASS" / "RECORDED SESSION") so it's
 *     never ambiguous which one a student is looking at.
 *
 * Shared between the Dashboard (signed-in, `community` comes from
 * `myProgress()`'s already-fetched response — no extra request) and the
 * Landing page (signed-out, `community` comes from the public
 * `communityConfig()` call) — one component, two callers, so the two
 * surfaces can never quietly drift apart.
 */
export default function CommunityLiveLearning({ t, lang, community }) {
  return (
    <div className="mt-10" data-testid="community-live-learning">
      <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937]">
        {t("dashboard.community_live_title")}
      </h2>
      <p className="text-[#4B5563] mt-2">{t("dashboard.community_live_subtitle")}</p>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <WhatsAppChannelCard t={t} />
        <ZoomClassCard t={t} lang={lang} community={community || {}} />
      </div>
    </div>
  );
}

function WhatsAppChannelCard({ t }) {
  return (
    <div
      data-testid="community-whatsapp-card"
      className="rounded-2xl bg-white border border-[#E7E0D2] p-4 hover:border-[#10B981] transition">
      <div className="w-9 h-9 rounded-lg bg-[#ECFDF5] text-[#10B981] grid place-items-center">
        <WhatsAppIcon className="w-4 h-4" />
      </div>
      <h3 className="font-heading text-base font-semibold text-[#1F2937] mt-3">{t("dashboard.whatsapp_title")}</h3>
      <p className="text-sm text-[#4B5563] mt-1.5 leading-relaxed">{t("dashboard.whatsapp_desc")}</p>
      <a
        href={WHATSAPP_CHANNEL_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="community-whatsapp-cta"
        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#10B981] hover:bg-[#0D9668] text-white text-sm font-medium transition">
        {t("dashboard.whatsapp_cta")} <ArrowRight className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}

function ZoomClassCard({ t, lang, community }) {
  // Defence in depth: the backend already rejects non-https values on save
  // (routes/admin.py), but a stale/legacy config document could still hold
  // something else — never render an unsafe href.
  const safeZoomUrl = community.zoom_enabled && isSafeHttpsUrl(community.zoom_url) ? community.zoom_url : null;
  const schedule = safeZoomUrl ? formatZoomDateTime(community.zoom_scheduled_at, lang) : null;
  // A recording is only ever shown as a fallback for "nothing upcoming" —
  // an admin-scheduled class always wins the card's one slot. Disabling the
  // class (or it not being set) is what surfaces the recording, so a
  // student never sees a live-join button and a stale recording link
  // competing for attention at the same time.
  const safeRecordingUrl = !safeZoomUrl && community.recording_enabled && isSafeHttpsUrl(community.recording_url)
    ? community.recording_url
    : null;

  const iconWellCls = safeZoomUrl
    ? "bg-[#EFF6FF] text-[#1D4ED8]"
    : safeRecordingUrl
      ? "bg-[#FFF7ED] text-[#D97706]"
      : "bg-[#EFF6FF] text-[#1D4ED8]";

  return (
    <div
      data-testid="community-zoom-card"
      className="rounded-2xl bg-white border border-[#E7E0D2] p-4 hover:border-[#1D4ED8] transition">
      <div className={`w-9 h-9 rounded-lg grid place-items-center ${iconWellCls}`}>
        {safeRecordingUrl ? <PlayCircle className="w-4 h-4" /> : <Video className="w-4 h-4" />}
      </div>

      {/* The "message above" that says which kind of thing this card is
          showing — never just a bare title, so an upcoming class and a
          past recording can never be mistaken for each other. */}
      <div
        className={`text-[10px] uppercase tracking-[0.16em] font-semibold mt-3 ${
          safeZoomUrl ? "text-[#1D4ED8]" : safeRecordingUrl ? "text-[#D97706]" : "text-[#9CA3AF]"
        }`}
        data-testid="community-zoom-eyebrow">
        {safeZoomUrl
          ? t("dashboard.zoom_upcoming_badge")
          : safeRecordingUrl
            ? t("dashboard.recording_badge")
            : t("dashboard.zoom_no_session_badge")}
      </div>

      <h3 className="font-heading text-base font-semibold text-[#1F2937] mt-1">
        {safeZoomUrl
          ? community.zoom_title || t("dashboard.zoom_title")
          : safeRecordingUrl
            ? community.recording_title || t("dashboard.recording_title")
            : t("dashboard.zoom_title")}
      </h3>

      {safeZoomUrl ? (
        <>
          <p className="text-sm text-[#4B5563] mt-1.5 leading-relaxed">
            {community.zoom_description || t("dashboard.zoom_active_desc")}
          </p>
          {schedule && (
            <div className="mt-2 flex flex-col gap-0.5 text-sm text-[#1F2937]" data-testid="community-zoom-schedule">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5 text-[#1D4ED8]" /> {schedule.dateLabel}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="w-3.5 h-3.5 text-[#1D4ED8]" /> {schedule.timeLabel}
              </span>
            </div>
          )}
          <a
            href={safeZoomUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="community-zoom-cta"
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white text-sm font-medium transition">
            {t("dashboard.zoom_cta")} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </>
      ) : safeRecordingUrl ? (
        <>
          <p className="text-sm text-[#4B5563] mt-1.5 leading-relaxed">
            {community.recording_description || t("dashboard.recording_desc")}
          </p>
          <a
            href={safeRecordingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="community-recording-cta"
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white text-sm font-medium transition">
            {t("dashboard.recording_cta")} <PlayCircle className="w-3.5 h-3.5" />
          </a>
        </>
      ) : (
        <>
          {/* Card stays the same height/shape whether a class is scheduled or
              not, so the layout doesn't jump when the admin toggles it —
              same reasoning as ThreeLayerCards showing a "not submitted"
              badge instead of hiding the Official Certification card. */}
          <p className="text-sm text-[#4B5563] mt-1.5 leading-relaxed">{t("dashboard.zoom_empty_desc")}</p>
          <span
            data-testid="community-zoom-inactive"
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F5EFE3] text-[#4B5563] text-sm font-medium cursor-default">
            {t("dashboard.zoom_stay_tuned")}
          </span>
        </>
      )}
    </div>
  );
}

function isSafeHttpsUrl(url) {
  if (!url) return false;
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
}

/** ISO-8601 UTC -> viewer's own local date/time, in their current UI
 * language — mirrors the toLocaleDateString() convention already used for
 * `achievements.earned_on`, so a globally-distributed audience each see the
 * class at their own correct local time rather than the admin's. The time
 * label is suffixed with a zone abbreviation ("7:00 PM IST") so it reads as
 * an unambiguous instant rather than a bare clock time. */
function formatZoomDateTime(iso, lang) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    const viewerZone = browserTimezone();
    const abbrev = timezoneAbbreviation(d, viewerZone);
    const time = d.toLocaleTimeString(lang, { hour: "numeric", minute: "2-digit" });
    return {
      dateLabel: d.toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long" }),
      timeLabel: abbrev ? `${time} ${abbrev}` : time,
    };
  } catch {
    return { dateLabel: d.toLocaleDateString(), timeLabel: d.toLocaleTimeString() };
  }
}
